import { api } from './api';

// Type definitions matching backend schemas
export interface ProgramParticipation {
  enrolled_clients: number;
  active_completion_rate: number;
  certification_attainment_rate: number;
}

export interface RiskReadiness {
  readiness_score: number;
  bcp_completeness_percent: number;
  exercise_completion_rate: number;
  average_response_time_minutes: number;
  training_completion_rate: number;
}

export interface IncidentTypeCount {
  incident_type: string;
  count: number;
}

export interface TopIncidents {
  incidents: IncidentTypeCount[];
}

export interface StateBenchmark {
  state: string;
  readiness_score: number;
  industry: string;
  sample_size: number;
}

export interface BenchmarkingMap {
  states: StateBenchmark[];
}

export interface MonthlyResponseTime {
  month: string;
  year: number;
  average_response_time_minutes: number;
  incident_count: number;
}

export interface ThreatIncidents {
  monthly_data: MonthlyResponseTime[];
}

export interface IndustryTrainingCompletion {
  industry: string;
  completion_rate: number;
  total_users: number;
  completed_users: number;
}

export interface TrainingCompletion {
  by_industry: IndustryTrainingCompletion[];
}

export interface ClaimsPrevention {
  claim_frequency_differential_percent: number;
  average_claim_severity_reduction_percent: number;
  baseline_period_frequency: number;
  current_period_frequency: number;
  baseline_avg_severity: number;
  current_avg_severity: number;
}

export interface IndustryResilience {
  industry: string;
  resilience_score: number;
  readiness_score: number;
  training_completion_rate: number;
  exercise_completion_rate: number;
}

export interface ResilienceByIndustry {
  industries: IndustryResilience[];
}

export interface RetentionROI {
  retention_impact_percent: number;
  new_leads_generated: number;
  policyholder_retention_rate: number;
  total_policyholders: number;
}

export interface DashboardData {
  program_participation: ProgramParticipation;
  risk_readiness: RiskReadiness;
  top_incidents: TopIncidents;
  benchmarking_map: BenchmarkingMap;
  threat_incidents: ThreatIncidents;
  training_completion: TrainingCompletion;
  claims_prevention: ClaimsPrevention;
  resilience_by_industry: ResilienceByIndustry;
  retention_roi: RetentionROI;
}

class AnalyticsService {
  private baseUrl = '/v1/analytics';

  async getProgramParticipation(): Promise<ProgramParticipation> {
    const response = await api.get<ProgramParticipation>(`${this.baseUrl}/program-participation`);
    return response.data;
  }

  async getRiskReadiness(): Promise<RiskReadiness> {
    const response = await api.get<RiskReadiness>(`${this.baseUrl}/risk-readiness`);
    return response.data;
  }

  async getTopIncidents(limit: number = 5): Promise<TopIncidents> {
    const response = await api.get<TopIncidents>(`${this.baseUrl}/top-incidents?limit=${limit}`);
    return response.data;
  }

  async getBenchmarkingMap(): Promise<BenchmarkingMap> {
    const response = await api.get<BenchmarkingMap>(`${this.baseUrl}/benchmarking-map`);
    return response.data;
  }

  async getThreatIncidents(): Promise<ThreatIncidents> {
    const response = await api.get<ThreatIncidents>(`${this.baseUrl}/threat-incidents`);
    return response.data;
  }

  async getTrainingCompletion(): Promise<TrainingCompletion> {
    const response = await api.get<TrainingCompletion>(`${this.baseUrl}/training-completion`);
    return response.data;
  }

  async getClaimsPrevention(): Promise<ClaimsPrevention> {
    const response = await api.get<ClaimsPrevention>(`${this.baseUrl}/claims-prevention`);
    return response.data;
  }

  async getResilienceByIndustry(): Promise<ResilienceByIndustry> {
    const response = await api.get<ResilienceByIndustry>(`${this.baseUrl}/resilience-by-industry`);
    return response.data;
  }

  async getRetentionROI(): Promise<RetentionROI> {
    const response = await api.get<RetentionROI>(`${this.baseUrl}/retention-roi`);
    return response.data;
  }

  async getDashboard(): Promise<DashboardData> {
    const response = await api.get<DashboardData>(`${this.baseUrl}/dashboard`);
    return response.data;
  }

  getDemoDashboard(): DashboardData {
    return {
      program_participation: {
        enrolled_clients: 1250,
        active_completion_rate: 78,
        certification_attainment_rate: 45,
      },
      risk_readiness: {
        readiness_score: 73,
        bcp_completeness_percent: 85,
        exercise_completion_rate: 72,
        average_response_time_minutes: 18.5,
        training_completion_rate: 78,
      },
      top_incidents: {
        incidents: [
          { incident_type: 'cyberattack', count: 245 },
          { incident_type: 'power_outage', count: 189 },
          { incident_type: 'severe_weather', count: 156 },
          { incident_type: 'hardware_failure', count: 134 },
          { incident_type: 'supply_chain', count: 98 },
        ],
      },
      benchmarking_map: {
        states: [
          { state: 'CA', readiness_score: 82, industry: 'Technology', sample_size: 150 },
          { state: 'NY', readiness_score: 78, industry: 'Finance', sample_size: 120 },
          { state: 'TX', readiness_score: 75, industry: 'Energy', sample_size: 95 },
          { state: 'FL', readiness_score: 71, industry: 'Healthcare', sample_size: 88 },
          { state: 'IL', readiness_score: 69, industry: 'Manufacturing', sample_size: 75 },
          { state: 'PA', readiness_score: 68, industry: 'Retail', sample_size: 65 },
          { state: 'OH', readiness_score: 66, industry: 'Manufacturing', sample_size: 58 },
          { state: 'GA', readiness_score: 64, industry: 'Logistics', sample_size: 52 },
          { state: 'NC', readiness_score: 63, industry: 'Technology', sample_size: 48 },
          { state: 'MI', readiness_score: 61, industry: 'Automotive', sample_size: 42 },
        ],
      },
      threat_incidents: {
        monthly_data: [
          { month: 'April', year: 2024, average_response_time_minutes: 55, incident_count: 12 },
          { month: 'May', year: 2024, average_response_time_minutes: 40, incident_count: 15 },
          { month: 'June', year: 2024, average_response_time_minutes: 35, incident_count: 18 },
          { month: 'July', year: 2024, average_response_time_minutes: 30, incident_count: 22 },
          { month: 'August', year: 2024, average_response_time_minutes: 32, incident_count: 20 },
          { month: 'September', year: 2024, average_response_time_minutes: 35, incident_count: 16 },
        ],
      },
      training_completion: {
        by_industry: [
          { industry: 'Retail', completion_rate: 60, total_users: 240, completed_users: 144 },
          { industry: 'Manufacturing', completion_rate: 70, total_users: 180, completed_users: 126 },
          { industry: 'Healthcare', completion_rate: 75, total_users: 320, completed_users: 240 },
          { industry: 'Finance', completion_rate: 80, total_users: 280, completed_users: 224 },
        ],
      },
      claims_prevention: {
        claim_frequency_differential_percent: -16,
        average_claim_severity_reduction_percent: -24,
        baseline_period_frequency: 450,
        current_period_frequency: 378,
        baseline_avg_severity: 125000,
        current_avg_severity: 95000,
      },
      resilience_by_industry: {
        industries: [
          { industry: 'Technology', resilience_score: 88, readiness_score: 85, training_completion_rate: 82, exercise_completion_rate: 90 },
          { industry: 'Finance', resilience_score: 85, readiness_score: 83, training_completion_rate: 85, exercise_completion_rate: 87 },
          { industry: 'Healthcare', resilience_score: 79, readiness_score: 76, training_completion_rate: 78, exercise_completion_rate: 81 },
          { industry: 'Manufacturing', resilience_score: 72, readiness_score: 70, training_completion_rate: 72, exercise_completion_rate: 74 },
          { industry: 'Retail', resilience_score: 68, readiness_score: 65, training_completion_rate: 65, exercise_completion_rate: 71 },
        ],
      },
      retention_roi: {
        retention_impact_percent: 6.3,
        new_leads_generated: 215,
        policyholder_retention_rate: 94.2,
        total_policyholders: 12500,
      },
    };
  }
}

export const analyticsService = new AnalyticsService();

